import * as signalR from '@microsoft/signalr';
import { AuthService } from '@portal-core/auth/services/auth.service';
import { firstValueFrom } from 'rxjs';

/**
 * Custom SignalR HttpClient that automatically retries requests after a 401 response
 * by invoking the application's refresh token endpoint.
 */
export class DataStreamSignalRHttpClient implements signalR.HttpClient {
  constructor(private authService: AuthService) {}

  get(url: string, options?: signalR.HttpRequest): Promise<signalR.HttpResponse> {
    return this.send({ ...options, method: 'GET', url });
  }

  post(url: string, options?: signalR.HttpRequest): Promise<signalR.HttpResponse> {
    return this.send({ ...options, method: 'POST', url });
  }

  delete(url: string, options?: signalR.HttpRequest): Promise<signalR.HttpResponse> {
    return this.send({ ...options, method: 'DELETE', url });
  }

  getCookieString(_url: string): string {
    if (typeof document !== 'undefined') {
      return document.cookie ?? '';
    }

    return '';
  }

  async send(request: signalR.HttpRequest): Promise<signalR.HttpResponse> {
    if (!request?.url) {
      throw new Error('HttpRequest.url is required');
    }

    const headers = this.getHeaderObject(request.headers);
    const body = this.serializeBody(request.content);

    const fetchOptions: RequestInit = {
      method: request.method || 'GET',
      headers,
      body,
      credentials: 'include',
      cache: 'no-store',
      signal: request.abortSignal as any
    };

    let response = await fetch(request.url, fetchOptions);

    if (response.status === 401) {
      await firstValueFrom(this.authService.refreshToken$());
      response = await fetch(request.url, fetchOptions);
    }

    return this.wrapResponse(response);
  }

  private getHeaderObject(headers?: Record<string, string | undefined> | Headers): Record<string, string> {
    if (!headers) {
      return {};
    }

    if (headers instanceof Headers) {
      const result: Record<string, string> = {};
      headers.forEach((value, key) => {
        result[key] = value;
      });

      return result;
    }

    const result: Record<string, string> = {};
    for (const key of Object.keys(headers)) {
      const value = (headers as any)[key];
      if (typeof value === 'string') {
        result[key] = value;
      }
    }

    return result;
  }

  private serializeBody(content?: any): BodyInit | undefined {
    if (content == null) {
      return undefined;
    }

    if (typeof content === 'string' || content instanceof Blob || content instanceof ArrayBuffer || content instanceof URLSearchParams) {
      return content as BodyInit;
    }

    if (typeof content === 'object') {
      return JSON.stringify(content);
    }

    return content as BodyInit;
  }

  private async wrapResponse(response: Response): Promise<signalR.HttpResponse> {
    const content = await response.text();

    const result: signalR.HttpResponse = {
      statusCode: response.status,
      statusText: response.statusText,
      content
    };

    return result;
  }
}
